import type { FC, ReactNode } from 'react'
import { useMemo } from 'react'

import { Box, Tooltip, Typography, useTheme } from '@mui/material'

import type { ConversationTurnEntry } from '@/index'
import { formatTimestamp } from '@/common/utils'
import {
  resolveEventTypeColorFor,
  resolveEventTypeLabelFor,
} from '@/features/events/shared/types/enums/EventType'
import { CopyButton, standardTooltipProps } from '@/common/components'

import { extractEventContent } from '../utils'
import { DEFAULT_BUBBLE_STYLES } from './defaults'
import { createBubblePaperSx, footerContainerSx, copyButtonContainerSx } from './styles'
import {
  bubbleContainerSx,
  clickableEventLabelSx,
  eventLabelSx,
  timestampSx,
} from './BaseBubble.styles'

interface BaseBubbleProps {
  readonly event: ConversationTurnEntry
  readonly showTimestamp?: boolean
  readonly onEventDetailsClick?: ((event: ConversationTurnEntry) => void) | undefined
  readonly borderColor?: string | undefined
  readonly renderFooter?: ((borderColor: string) => ReactNode) | undefined
  readonly children: ReactNode
}

export const BaseBubble: FC<BaseBubbleProps> = ({
  event,
  showTimestamp = true,
  onEventDetailsClick,
  borderColor: customBorderColor,
  renderFooter,
  children,
}) => {
  const theme = useTheme()
  const borderColor = customBorderColor || resolveEventTypeColorFor(event.eventType, theme)
  const copyContent = useMemo(() => extractEventContent(event), [event])

  const defaultFooter = (
    <Box className="bubble-footer" sx={footerContainerSx}>
      {onEventDetailsClick ? (
        <Tooltip {...standardTooltipProps} title="Event details">
          <Typography
            className="event-details-link"
            variant="caption"
            onClick={() => onEventDetailsClick(event)}
            sx={clickableEventLabelSx(borderColor)}
          >
            {resolveEventTypeLabelFor(event.eventType)}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="caption" sx={eventLabelSx(borderColor)}>
          {resolveEventTypeLabelFor(event.eventType)}
        </Typography>
      )}
      <Box className="copy-button" sx={copyButtonContainerSx}>
        <CopyButton text={copyContent} variant="small" />
      </Box>
    </Box>
  )

  return (
    <Box sx={bubbleContainerSx}>
      <Box
        sx={createBubblePaperSx(theme, borderColor, 'left', DEFAULT_BUBBLE_STYLES.bubbles.agent)}
      >
        {children}
        {renderFooter ? renderFooter(borderColor) : defaultFooter}
      </Box>

      {showTimestamp && (
        <Typography variant="caption" color="text.secondary" sx={timestampSx}>
          {formatTimestamp(event.timestamp)}
        </Typography>
      )}
    </Box>
  )
}
